import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Col, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Button } from "@shopify/polaris";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { BsFillInfoCircleFill } from "react-icons/bs";

import FieldsDropdown from "../FieldsDropdown";

import { setIsContactSyncRulesAndDefaultMappings } from "../../store/slices/contactSlice";
import {
  setShowErrorModal,
  setShowSuccessModal,
} from "../../store/slices/preferenceSlice";

import { getUrl } from "../../helpers/url";

import { useCmsHook } from "../../helpers/cmsHook";

const DefaultMappings = () => {
  const { cmsInstance } = useCmsHook();

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const {
    paymentTermPreference,
    currencyPreference,
    customerPostingGroupPreference,
    vatBusinessPostingGroupPreference,
    generalBusinessPostingGroupPreference,
    generalProductPostingGroupPreference,
    productTypePreference,
    msbcDefaultFields: {
      loading,
      msbcDefaultFields: {
        paymentTerms,
        currencies,
        customerPostingGroups,
        vatPostingGroups,
        generalBusPostingGroups,
        generalProductPostingGroups,
        productTypes,
      },
    },
  } = useSelector((state) => state.contact);

  const { tenantId } = useSelector((state) => state.preference);

  return (
    <div>
      {loading || !cmsInstance ? (
        <div
          className="d-flex flex-row justify-content-center align-items-center my-3"
          style={{ height: "40vh" }}
        >
          <Spinner
            animation="border"
            variant="secondary"
            role="status"
            size="md"
          ></Spinner>
        </div>
      ) : (
        <div className="px-3">
          <div className="sync-settings-card py-5">
            {cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.syncSettings.contact.defaultMappings"
              )}
            <div>
              <Col
                md={9}
                lg={7}
                className="d-flex flex-row justify-content-between align-items-center mt-3"
              >
                <p className="dropdown-header">
                  Payment Terms{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip style={{ fontSize: "12px" }}>
                        Define the standard terms you offer to your customers
                        for invoice payment
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </p>
                {paymentTerms && paymentTerms.length > 0 && (
                  <FieldsDropdown
                    fieldsData={paymentTerms}
                    dropdownFor="paymentTerms"
                    selectedPreference={paymentTermPreference}
                    setWidth={true}
                  />
                )}
              </Col>
              <Col
                md={8}
                lg={7}
                className="d-flex justify-content-between align-items-center mt-3"
              >
                <p className="dropdown-header">
                  Currency{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        Set the default currency in which the transactions will
                        be carried out.
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </p>
                {currencies && currencies.length > 0 && (
                  <FieldsDropdown
                    fieldsData={currencies}
                    dropdownFor="currency"
                    selectedPreference={currencyPreference}
                    setWidth={true}
                  />
                )}
              </Col>
              <Col
                md={8}
                lg={7}
                className="d-flex justify-content-between align-items-center mt-3"
              >
                <p className="dropdown-header">
                  Customer Posting Group{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        This field groups customers together to manage how
                        transactions are posted to your general ledger. Choose a
                        default group for new customers
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </p>
                {customerPostingGroups && customerPostingGroups.length > 0 && (
                  <FieldsDropdown
                    fieldsData={customerPostingGroups}
                    dropdownFor="customerPostingGroup"
                    selectedPreference={customerPostingGroupPreference}
                    setWidth={true}
                  />
                )}
              </Col>
              <Col
                md={8}
                lg={7}
                className="d-flex justify-content-between align-items-center mt-3"
              >
                <p className="dropdown-header">
                  VAT Business Posting Group{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        This field is used for managing VAT treatment in
                        Business Central. The VAT business posting group you
                        select here will be set as the default for all new
                        customers
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </p>
                {vatPostingGroups && vatPostingGroups.length > 0 && (
                  <FieldsDropdown
                    fieldsData={vatPostingGroups}
                    dropdownFor="vatPostingGroup"
                    selectedPreference={vatBusinessPostingGroupPreference}
                    setWidth={true}
                  />
                )}
              </Col>
              <Col
                md={8}
                lg={7}
                className="d-flex justify-content-between align-items-center mt-3"
              >
                <p className="dropdown-header">
                  General Business Posting Group{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        This field is also used to manage how transactions are
                        posted to the general ledger, but on a broader scale
                        than the Customer Posting Group. Select a default group
                        for new customers
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </p>
                {generalBusPostingGroups &&
                  generalBusPostingGroups.length > 0 && (
                    <FieldsDropdown
                      fieldsData={generalBusPostingGroups}
                      dropdownFor="generalBusinessPostingGroup"
                      selectedPreference={generalBusinessPostingGroupPreference}
                      setWidth={true}
                    />
                  )}
              </Col>
              <Col
                md={8}
                lg={7}
                className="d-flex justify-content-between align-items-center mt-3"
              >
                <p className="dropdown-header">
                  General Product Posting Groups{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        This field is used for managing the Product treatment in
                        Business Central. The General Product Posting Groups
                        will you select here will set as the default for all new
                        customer
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </p>
                {generalProductPostingGroups &&
                  generalProductPostingGroups.length > 0 && (
                    <FieldsDropdown
                      fieldsData={generalProductPostingGroups}
                      dropdownFor="generalProductPostingGroup"
                      selectedPreference={generalProductPostingGroupPreference}
                      setWidth={true}
                    />
                  )}
              </Col>
              <Col
                md={8}
                lg={7}
                className="d-flex justify-content-between align-items-center mt-3"
              >
                <p className="dropdown-header">
                  Product Types{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip>
                        This field is used for managing the Product Types
                        treatment in Business Central. The Product Types will
                        you select here will set as the default for all new
                        customer
                      </Tooltip>
                    }
                  >
                    <span>
                      <BsFillInfoCircleFill />
                    </span>
                  </OverlayTrigger>
                </p>
                {productTypes && productTypes.length > 0 && (
                  <FieldsDropdown
                    fieldsData={productTypes}
                    dropdownFor="productType"
                    selectedPreference={productTypePreference}
                    setWidth={true}
                  />
                )}
              </Col>
            </div>
          </div>
          <div className="d-flex flex-row justify-content-end mt-4 mb-2">
            {!paymentTermPreference ||
            !currencyPreference ||
            !customerPostingGroupPreference ||
            !vatBusinessPostingGroupPreference ||
            !generalBusinessPostingGroupPreference ||
            !generalProductPostingGroupPreference ||
            !productTypePreference ? (
              <OverlayTrigger
                placement="left"
                overlay={
                  <Tooltip style={{ fontSize: "12px" }}>
                    {showTooltipText(
                      paymentTermPreference,
                      currencyPreference,
                      customerPostingGroupPreference,
                      vatBusinessPostingGroupPreference,
                      generalBusinessPostingGroupPreference,
                      generalProductPostingGroupPreference,
                      productTypePreference
                    )}
                  </Tooltip>
                }
              >
                <div>
                  <Button
                    style={{ cursor: "not-allowed" }}
                    className="sync-settings-button"
                    disabled
                  >
                    Save changes
                  </Button>
                </div>
              </OverlayTrigger>
            ) : (
              <Button
                className="sync-settings-button"
                loading={isLoading}
                primary
                onClick={() =>
                  onSaveDefaultMappings({
                    setIsLoading,
                    searchParams,
                    paymentTermPreference,
                    currencyPreference,
                    customerPostingGroupPreference,
                    vatBusinessPostingGroupPreference,
                    generalBusinessPostingGroupPreference,
                    generalProductPostingGroupPreference,
                    productTypePreference,
                    dispatch,
                    cmsInstance,
                    tenantId,
                  })
                }
                disabled={isLoading}
              >
                Save changes
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DefaultMappings;

const showTooltipText = (
  paymentTermsCodePreference,
  currencyCodePreference,
  customerPostingGroupPreference,
  vatBusinessPostingGroupPreference,
  generalBusinessPostingGroupPreference,
  generalProductPostingGroupPreference,
  productTypePreference
) => {
  if (!paymentTermsCodePreference) {
    return "Please select payment terms code.";
  } else if (!currencyCodePreference) {
    return "Please select currency code.";
  } else if (!customerPostingGroupPreference) {
    return "Please select customer posting group.";
  } else if (!vatBusinessPostingGroupPreference) {
    return "Please select VAT business posting group.";
  } else if (!generalBusinessPostingGroupPreference) {
    return "Please select general business posting group.";
  } else if (!generalProductPostingGroupPreference) {
    return "Please select the general product posting group";
  } else if (!productTypePreference) {
    return "Please select the product types";
  }
};

export const onSaveDefaultMappings = async ({
  setIsLoading,
  searchParams,
  paymentTermPreference,
  currencyPreference,
  customerPostingGroupPreference,
  vatBusinessPostingGroupPreference,
  generalBusinessPostingGroupPreference,
  generalProductPostingGroupPreference,
  productTypePreference,
  dispatch,
  cmsInstance,
  tenantId,
}) => {
  try {
    setIsLoading(true);
    const shop = searchParams.get("shop");
    await axios.post(getUrl("REACT_APP_SAVE_PREFERENCES"), {
      shop,
      tenantId,
      preferences: {
        msbcFields: {
          paymentTerms: paymentTermPreference,
          customerPostingGroup: customerPostingGroupPreference,
          vatBusinessPostingGroup: vatBusinessPostingGroupPreference,
          generalBusinessPostingGroup: generalBusinessPostingGroupPreference,
          generalProductPostingGroup: generalProductPostingGroupPreference,
          productTypes: productTypePreference,
          currency: currencyPreference,
        },
      },
      preferenceType: "customer",
    });
    dispatch(
      setIsContactSyncRulesAndDefaultMappings({ type: "defaultMappings" })
    );

    dispatch(
      setShowSuccessModal({
        message:
          cmsInstance &&
          cmsInstance.sendData().cmsContent.notifications.syncSettings
            .successContent.contactDefaultMappings.message,
      })
    );
  } catch (error) {
    console.log(error);
    let errorMessage;
    if (error.response && error.response.data.message)
      errorMessage = error.response.data.message;
    else errorMessage = error.message;
    dispatch(setShowErrorModal({ message: errorMessage }));
  } finally {
    setIsLoading(false);
  }
};
